import { DataTablePaginatorPosition } from '@/interfaces/primeVueTypes';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { DateKey, Order } from 'types';

export type Filtering<T extends object> = {
  [K in keyof T]?: Extract<T[K], string | number | boolean | Date>;
};

export type PageOptions<T extends object> = {
  rows: number;
  page: number;
  perPage: number;
  sortBy?: keyof T;
  sortOrder?: Order;
};

export enum TableMode {
  /** Client side paging and filtering */
  CLIENT_SIDE = 'clientSide',
  /** Server side paging and filtering */
  LAZY = 'lazy',
}

export enum TablePaginationVariant {
  DEFAULT = 'default',
  CURSOR = 'cursor',
}

export type TableWrapperProps<T extends object, U extends object = T> = {
  expandableRows?: boolean;
  items: T[];
  fields: TableField<T>[];
  filterConfiguration?: FilterConfigurations<T, U>;
  mode?: TableMode;
  paginatorVariant?: TablePaginationVariant;
  /** Respected only when using `TablePaginationVariant.CURSOR` */
  hasNextPage?: boolean;
  actionIcon?: 'delete' | 'ban';
  actionIconDisabled?: boolean;
  actionIconTooltipText?: string;
  displayActionIcon?: boolean;
  selection?: boolean;
  /** Count of selected items. Provide if selecting rows on multiple pages */
  selectionCount?: number;
  rowClickable?: boolean;
  resetSelectionOnPageOrFilterChange?: boolean;
  paginator?: boolean;
  hideResetFilterButton?: boolean;
  emptyTableText?: string;
  paginatorPosition?: DataTablePaginatorPosition;
};

export type TableField<T extends object> = {
  key: keyof T;
  sortable: boolean;
  tdClass?: string[] | CellStyleFunction<T>;
  cellClassGetter?: CellStyleFunction<T>;
  width?: string;
  valueTranslationKey?: string | undefined;
  valueGetter?: (item: T) => (typeof item)[keyof T];
  truncation?: {
    maxLength: number;
  };
  isVisible?: boolean;
  fluid?: boolean;
} & ({ label: string } | { icon: IconDefinition });

export type FilterConfigurations<T extends object, U extends object = T> = {
  [key in keyof T]?: FilterConfiguration<U>;
};

export type FilterConfiguration<T extends object> = {
  type: FilterType;
  width?: string;
  customLabel?: string;
  simpleLabel?: boolean;
  translationKey?: string;
  filterProperty?: keyof T;
} & (
  | {
      type: FilterType.INPUT;
    }
  | {
      type: FilterType.NUMBER;
    }
  | {
      type: FilterType.SELECT;
      selectOptions: string[];
    }
  | {
      type: FilterType.DATE_RANGE_PICKER;
      dateFromKey: DateKey<T>;
      dateToKey: DateKey<T>;
      filterProperty: never;
    }
  | {
      type: FilterType.CHECK_BOX;
    }
);

export type SortedFilters<T extends object, U extends object = T> = {
  inputFilters: FilterConfigurations<T, U>;
  selectFilters: FilterConfigurations<T, U>;
};

export type SelectOptions<T extends object> = Partial<
  Record<keyof T, string[]>
>;

export enum RowClass {
  SELECTED = 'selected-row',
  SELECTED_SECONDARY = 'selected-row-secondary',
  DANGER = 'danger-row',
}

export enum FilterType {
  INPUT = 'input',
  NUMBER = 'numberInput',
  SELECT = 'select',
  DATE_RANGE_PICKER = 'dateRangePicker',
  CHECK_BOX = 'checkBox',
}

export type CellStyleFunction<T> = (
  value: T[keyof T],
  key: keyof T,
  item: T,
) => string[];
